import { useGetAllOwnKPIQuery } from "@/app/features/employee/performance/performance.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon, LaptopPerformanceIcon, Parabola04Icon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { countParPageSerialNumber } from "@/utils/global.utils";
import { Button, Flex, type TableProps } from "antd";
import LogViewer from "./LogViewer";
import ManageOwnKPI from "./ManageOwnKpi";
import ViewOwnKPI from "./ViewOwnKPI";

const KPI_STATUS_CONFIG = [
  {
    label: "Draft",
    value: "DRAFT",
    className: "status status-slate",
  },
  {
    label: "Pending",
    value: "PENDING",
    className: "status status-yellow",
  },
  {
    label: "Returned",
    value: "RETURNED",
    className: "status status-fuchsia",
  },
  {
    label: "Approved By Line Manager",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

const OwnKPI = () => {
  const { userId, organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.EMP_OWN_KPI,
  );
  const [config, handleOpen, handleClose] = useDiscloser<
    "VIEW_DETAILS" | "ADD" | "VIEW" | "EDIT"
  >();

  const { data, isFetching } = useGetAllOwnKPIQuery({
    organization_id: organizationId,
    employee_id: userId,
    page,
    limit,
    ...filters,
  });

  const kpis = data?.data?.kpis || [];
  const total = data?.data?.pagination?.total || 0;

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL. No.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Name",
      dataIndex: ["plan", "planName"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Plan Period",
      dataIndex: ["plan", "planPeriod"],
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Plan Type",
      dataIndex: ["plan", "planType"],
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Status",
      dataIndex: "kpiStatus",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          config={KPI_STATUS_CONFIG}
          shouldCapitalize={false}
        />
      ),
    },
    {
      title: "Due Date",
      dataIndex: "kpiDueDate",
      align: "center",
      render: (val: string) => (val ? formatDate(val) : "N/A"),
    },
    {
      width: 200,
      title: "Actions",
      render: (record: any) => (
        <Flex gap={8}>
          <Button
            size="small"
            disabled={record.kpiStatus === "PENDING"}
            className="action-btn"
            icon={<EyeIcon size={20} />}
            onClick={() => {
              if (record.kpiStatus === "PENDING") return;
              if (
                record.kpiStatus === "DRAFT" ||
                record.kpiStatus === "RETURNED"
              ) {
                handleOpen({
                  type: "EDIT",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                    planId: record.planId,
                  },
                });
              } else {
                handleOpen({
                  type: "VIEW_DETAILS",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                    planId: record.planId,
                  },
                });
              }
            }}
          />
          {/* {(record.kpiStatus === "DRAFT" ||
            record.kpiStatus === "RETURNED") && (
            <Button
              size="small"
              className="action-btn"
              icon={<PencilEdit02Icon size={20} />}
              onClick={() =>
                handleOpen({
                  type: "EDIT",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                    planId: record.planId,
                  },
                })
              }
            />
          )} */}
          {record.kpiStatus === "PENDING" && (
            <Button
              size="small"
              type="default"
              className="btn-primary h-8"
              onClick={() =>
                handleOpen({
                  type: "ADD",
                  data: {
                    planName: record.plan?.planName,
                    mappingId: record.id,
                    planId: record.planId,
                  },
                })
              }
            >
              <Parabola04Icon className="text-neutral-700 size-4" /> KPI Se-Up
            </Button>
          )}
          <Button
            size="small"
            type="default"
            className="btn-primary-bordered h-8"
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: {
                  planName: record.plan?.planName,
                  mappingId: record.id,
                  dateTime: formatDate(record.createdAt),
                },
              })
            }
          >
            <LaptopPerformanceIcon className="text-theme-primary size-4" /> Log
            View
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={kpis}
        onPaginate={handlePagination}
        customTitle="Own KPI List"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={total}
        filterSection={<></>}
      />

      <LogViewer
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        id={config.data?.mappingId}
        planName={config.data?.planName}
        dateTime={config.data?.dateTime}
        logs={config.data?.logData}
      />

      <ManageOwnKPI
        open={config.open && config.type === "ADD"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        planId={config.data?.planId}
      />

      <ManageOwnKPI
        open={config.open && config.type === "EDIT"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        planId={config.data?.planId}
        mode="edit"
      />

      <ViewOwnKPI
        open={config.open && config.type === "VIEW_DETAILS"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        source="OWN"
      />
    </>
  );
};

export default OwnKPI;
